import { FormContext, FormContextProvider } from '@stormwater-software/lib-form';
import { VersionContext } from '@stormwater-software/lib-ui';
import { get, has, isNull } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppContext from '../../../contexts/AppContext';
import InspectionContext from '../../../contexts/InspectionContext';
import ProjectContext from '../../../contexts/ProjectContext';
import ProjectPermissionContext from '../../../contexts/ProjectPermissionContext';
import { RolesContext } from '../../../contexts/RolesContext';
import { VersionContextProvider } from '../../../contexts/VersionContext';
import { useFindingActions } from '../../../hooks/finding';
import findingApi from '../../../utils/api/finding';
import { ActionButtonsWithScrollFix } from '../../Shared/ActionButtons';
import ToastError from '../../Shared/ToastError/ToastError';
import DeleteFindingModal from './DeleteFindingModal';
import FindingsForm, {
  extractFindingData,
  toImageUpload,
} from './FindingsForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import FindingReopenConfirmationModal from './FindingReopenConfirmationModal';
import DashboardContext from '../../../contexts/DashboardContext';
import moment from "moment";
import { parse, isAfter } from "date-fns";
import ClearComplianceComfirmationModal from './ClearComplianceConfirmationModal';
import { AppDivisionContext } from "../../../contexts/AppDivisionContext";
import inspectionApi from '../../../utils/api/inspection';
import clientApi from '../../../utils/api/client';
import schedulerApi from '../../../utils/api/inspectionSchedular';
import {
  backgroundPhotoUploads,
  jobFindingIds,
} from '../../../utils/backgroundPhotoUploads';
import { useOnPhotoUploadDone } from '../../../hooks/backgroundUploads';

function FindingDetailFormUI({
  onBackClick,
  inspectionId,
  finding,
  onSubmit,
  onDelete,
  findingNumber,
  isGuest,

  ...props
}) {
  const formContext = useContext(FormContext);
  const inspectionContext = useContext(InspectionContext);
  const projectContext = useContext(ProjectContext);
  const projectPermissionContext = useContext(ProjectPermissionContext);
  const permCheck = useContext(RolesContext).userHasPermission;
  const [findingId, setFindingId] = useState(finding.id);
  const [disableNext, setDisableNext] = useState(false);
  const [disablePrev, setDisablePrev] = useState(false);
  const inspectionComplete = inspectionContext.readOnlyFindings;
  const [isCompleted, setIsCompleted] = useState(
    props.isCompleted || inspectionComplete,
  );
  const [isFindingsDateCompleted, setIsFindingsDateCompleted] = useState(props.isFindingsDateCompleted);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showReopenConfirmationModal, setShowReopenConfirmationModal] =
    useState(false);
  const [inspectionCertifiedBefore, setInspectionCertifiedBefore] = useState([]);
  const [typeChangeAffectedInspections, setTypeChangeAffectedInspections] = useState([]);
  const [noMetadataImage, setNoMetadataImage] = useState([]);
  const [showMetadataWarning, setShowMetadataWarning] = useState(false);
  const [isEnabledPhotoMetadata, setIsEnabledPhotoMetadata] = useState(false);
  const [requireClosurePhotos, setRequireClosurePhotos] = useState(false);

  useEffect(() => {
    (async () => {
      const project = projectContext.project;
      const insp = inspectionContext.inspection;
      if (!project?.client_id) return;
      try {
        const c = await clientApi.getCustomFields(project.client_id, ['require_closure_photos']);
        if (c && c.require_closure_photos) {
          const projectId = project.id;
          if (projectId && insp?.template_id) {
            try {
              const formsData = await schedulerApi.getProjectForms(projectId);
              const matchingForm = formsData?.data?.find(
                (f) => f.template_id === insp.template_id,
              );
              const junctionFlag = matchingForm
                ? Boolean(matchingForm.require_closure_photos)
                : Boolean(project.require_closure_photos);
              setRequireClosurePhotos(junctionFlag);
            } catch {
              setRequireClosurePhotos(Boolean(project.require_closure_photos));
            }
          } else {
            setRequireClosurePhotos(Boolean(project.require_closure_photos));
          }
        }
      } catch {
        // ignore
      }
    })();
  }, [projectContext.project?.client_id, inspectionContext.inspection?.template_id]);

  const findingActions = useFindingActions({
    onDelete: () => setConfirmDelete(true),
    edit: true,
  });
  const history = useHistory();
  const { findings, inspection } = inspectionContext;
  const {
    date_completed: dateCompleted,
    completedImages,
  } = formContext.value;

  const closurePhotoNeeded = requireClosurePhotos && !!dateCompleted && (!completedImages || completedImages.length === 0);

  const isPublic = window.location.pathname.startsWith("/public")

  useEffect(() => {
    setIsCompleted(props.isCompleted || inspectionComplete)
    setIsFindingsDateCompleted(props.isFindingsDateCompleted)
  }, [props.isCompleted, props.isFindingsDateCompleted])

  useEffect(() => {
    const findingIds = findings.map((ele) => {
      return ele.id
    })

    if (findingIds[0] === findingId) {
      setDisablePrev(true);
      setDisableNext(false);
    }
    if (findingIds[findingIds.length - 1] === findingId) {
      setDisableNext(true);
      setDisablePrev(false);
    }
    if (findingIds[0] !== findingId && findingIds[findingIds.length - 1] !== findingId) {
      setDisablePrev(false);
      setDisableNext(false);
    }

  }, [findingId]);

  useEffect(() => {
    if (finding.finding_action) {
      formContext.set("actionPlans", finding.finding_action)
      formContext.set("actionObservation", finding.action_note ? finding.action_note.notes : null)
      formContext.set("isSigned", finding.action_note && finding.action_note.is_signed)
      formContext.set("actionId", finding.action_note && finding.action_note.id)
      formContext.set("signeeName", finding.action_note && finding.action_note.is_signed ? finding.action_note.commentableSignature.signee_name : null)
    }
  }, [])

  // SW2-2458: this finding's photos finished uploading in the background, so
  // show what the server now has (the photos, and the close for a closure job).
  useOnPhotoUploadDone(inspectionId, (job) => {
    if (!jobFindingIds(job).includes(Number(finding.id))) return;

    findingApi.show(finding.id).then((saved) => {
      if (!saved) return;

      const uploads = saved.finding_uploads || [];
      const { observations, completed_by } = extractFindingData(
        saved,
        inspectionId,
      );

      // As a normal save does: the server now holds the notes, so a later save
      // must send the action note's id, not create it again.
      formContext.set('observations', observations);
      formContext.set('completed_by', completed_by);
      formContext.set('actionPlans', saved.finding_action);
      formContext.set('actionId', saved.action_note_id && saved.action_note_id);

      formContext.set(
        'images',
        uploads.filter((image) => !image.is_completed).map(toImageUpload),
      );
      formContext.set(
        'completedImages',
        uploads.filter((image) => image.is_completed).map(toImageUpload),
      );
      setIsFindingsDateCompleted(!isNull(saved.date_completed));
      setIsCompleted(
        !isNull(saved.date_completed) ||
          !isNull(inspectionContext.inspection.compliance_date),
      );
    });
  });

  function handleSubmit() {
    const { date_completed, completedImages } = formContext.value;

    // SW2-2458: saving again while this finding's photos are still uploading
    // would send the same photos twice.
    if (backgroundPhotoUploads.pending({ findingId: finding.id }).length > 0) {
      toast.info(
        "This finding's photos are still uploading. Save again once they finish.",
      );

      return;
    }

    if (
      requireClosurePhotos &&
      date_completed &&
      (!completedImages || completedImages.length === 0)
    ) {
      toast.error(
        'At least one closure photo is required before closing this finding.',
      );

      return;
    }

    formContext.setBusy(true);

    const toastId = toast('Updating finding');

    return findingApi
      .update(
        finding.id,
        { ...formContext.value, inspectionId },
        { backgroundPhotos: true },
      )
      .then((updatedFinding) => {
        // SW2-2458: new closure photos — the save itself runs in the background
        // (the server needs the photos before it will close the finding).
        if (updatedFinding && updatedFinding.backgrounded) {
          // The job holds its own copy of the form; clear the comment as a
          // normal save does, so it is not posted again by the next save.
          formContext.set('comments', '');
          formContext.setBusy(false);
          toast.update(toastId, {
            render: 'The finding will save once its photos finish uploading',
            type: 'info',
            autoClose: 4000,
          });
          setInspectionCertifiedBefore([]);

          return undefined;
        }

        // reset comments/observations
        const { observations, completed_by } = extractFindingData(
          updatedFinding,
          inspectionId,
        );

        formContext.set('comments', '');
        formContext.set('observations', observations);
        formContext.set('completed_by', completed_by); // update field after save

        // set images from api
        if (updatedFinding.finding_uploads.filter(image => !image.is_completed).length > 0) {
          formContext.set(
            'images',
            updatedFinding.finding_uploads.filter(image => !image.is_completed).map(toImageUpload),
          );
        } else {
          formContext.set('images', [])
        }

        if (updatedFinding.finding_uploads.filter(image => image.is_completed).length > 0)
          formContext.set(
            'completedImages',
            updatedFinding.finding_uploads.filter(image => image.is_completed).map(toImageUpload),
          )
        else {
          formContext.set('completedImages', [])
        }

        formContext.set("actionPlans", updatedFinding.finding_action)
        formContext.set("actionId", updatedFinding.action_note_id && updatedFinding.action_note_id)

        // update context
        const { setFindings } = inspectionContext;

        const isBefore = inspection.created_date && moment(updatedFinding.date_completed).isBefore(moment(inspection.created_date).startOf('day'));

        if (isBefore) {
          setFindings(
            findings.filter((f) => f.id !== updatedFinding.id),
          );
        }
        else {
          setFindings(
            findings.map((f) =>
              f.id === updatedFinding.id ? updatedFinding : f,
            ),
          );
        }

        setIsCompleted(!isNull(updatedFinding.date_completed) || !isNull(inspection.compliance_date));
        setIsFindingsDateCompleted(!isNull(updatedFinding.date_completed))
        formContext.setBusy(false);

        toast.update(toastId, {
          render: 'Updated Finding',
          type: 'success',
        });
        setInspectionCertifiedBefore([]);

        return onSubmit(updatedFinding);
      })
      .catch(async (error) => {
        formContext.setBusy(false);

        let toastMessage = '';

        if (has(error, 'response.data.message')) {
          toastMessage = error.response.data.message;
        } else if (has(error, 'response.data')) {
          toastMessage = error.response.data;
        } else {
          toastMessage =
            'Cannot save finding due to an unknown error. Please try again later.';
        }

        toast.update(toastId, {
          render: (
            <ToastError
              message='Cannot update finding because of error: '
              error={toastMessage}
            />
          ),
          autoClose: false,
          type: 'error',
        });
        setIsCompleted(false);
        setIsFindingsDateCompleted(false)
        setInspectionCertifiedBefore([]);
        inspectionContext.reloadInspection();
      });
  }

  function handleEdit() {
    findings.forEach((find) => {
      if (find.number === findingNumber) {
        if (find.is_required_for_cert) {
          setShowReopenConfirmationModal(true);
        } else {
          setIsCompleted(false);
          setIsFindingsDateCompleted(false)
        }
      }
    })
  }

  function editText() {
    return permCheck('all', 'reopenFinding') || (permCheck("update", "Close Findings") && !dateCompleted) ?
      <React.Fragment>
        <FontAwesomeIcon icon={faPencilAlt} /> Edit Finding
      </React.Fragment> :
      null;
  }

  const extraButtonText = () => {
    return isFindingsDateCompleted ? 'Edit' : 'Save'
  }

  const handleExtraButtonClick = () => {
    switch (isFindingsDateCompleted) {
      case true:
        findings.forEach((find) => {
          if (find.number === findingNumber) {
            if (find.is_required_for_cert) {
              setShowReopenConfirmationModal(true);
            } else {
              setIsFindingsDateCompleted(false);
            }
          }
        })
        break

      case false:
        handleSubmit()
        break

      default: break
    }
  }

  // Date_completed → existing "certified before" check or direct submit.
  const runDateChecksAndSubmit = () => {
    if (!formContext.value.date_completed) {
      handleSubmit();
      return;
    }
    if (!finding.jt_insp_findings || finding.jt_insp_findings.length === 0) {
      handleSubmit();
      return;
    }
    const inspection_certifiedBefore = finding.jt_insp_findings.filter((ele) =>
      isAfter(parse(formContext.value.date_completed, 'MM-dd-yyyy', new Date()), ele.inspection.compliance_date),
    );
    if (inspection_certifiedBefore.length > 0) {
      setInspectionCertifiedBefore(
        inspection_certifiedBefore.map(
          (ele) =>
            `${ele.inspection.created_date} | ${ele.inspection.inspection_types?.map((t) => t.type).join(', ') || ''}`,
        ),
      );
    } else {
      handleSubmit();
    }
  };

  const handleSubmitClick = ({ skipCompliancePrecheck = false } = {}) => {
    if (isGuest && ((formContext.value.date_completed && !formContext.value.completed_by) || (!formContext.value.date_completed && formContext.value.completed_by))) {
      toast.error(`Please fill 'Completed By' field with your name along with 'Date Completed' to mark finding as complete`);
      return false;
    }

    // Pre-check: if the finding type changed, ask the backend whether the change would clear any compliance certs.
    if (!skipCompliancePrecheck && formContext.value.type && finding.type !== formContext.value.type) {
      findingApi
        .complianceImpact(finding.id, formContext.value.type, inspectionId)
        .then(({ affected_inspections }) => {
          if (affected_inspections && affected_inspections.length > 0) {
            setTypeChangeAffectedInspections(
              affected_inspections.map((insp) => {
                const createdDate = insp.created_date.split('-');
                const formattedDate = `${createdDate[1]}-${createdDate[2]}-${createdDate[0]}`;
                return `#${insp.number} | ${formattedDate}`;
              }),
            );
          } else {
            runDateChecksAndSubmit();
          }
        })
        .catch(() => runDateChecksAndSubmit());
      return;
    }

    runDateChecksAndSubmit();
  };

  const handleSubmitClickNoMetadata = () => {
    const noMetadataImage = formContext.value.images.filter((image) => {
      return image.gpsData === null;
    });

    const noMetadataCompletedImage = formContext.value.completedImages.filter((image) => {
      return image.gpsData === null;
    });

    if ((noMetadataImage.length > 0 || noMetadataCompletedImage.length > 0) && isEnabledPhotoMetadata) {
      setNoMetadataImage([...noMetadataImage, ...noMetadataCompletedImage]);
      setShowMetadataWarning(true);
    } else {
      handleSubmitClick();
    }
  };

  const findingIds = findings.map((ele) => {
    return ele.id
  })

  const getNextFinding = (id) => {
    const currentPosition = findingIds.indexOf(findingId)

    if (currentPosition !== findings.length - 1) {
      setFindingId(findingIds[currentPosition + 1]);
    }
    isPublic ? history.push(`/public/inspection/${inspectionId}/findings/${findingIds[currentPosition + 1]}/details`) : (isGuest)? history.push(`/guest/${projectContext.guestProjectUUID}/inspection/${inspectionId}/finding-details/${findingIds[currentPosition + 1]}`) : history.push(`/inspection/${inspectionId}/findings/${findingIds[currentPosition + 1]}/details`)
  };

  const getPrevFinding = (id) => {
    const currentPosition = findingIds.indexOf(findingId)

    if (currentPosition !== 0) {
      setFindingId(findingIds[currentPosition - 1]);
    }
    isPublic ? history.push(`/public/inspection/${inspectionId}/findings/${findingIds[currentPosition - 1]}/details`) : (isGuest)? history.push(`/guest/${projectContext.guestProjectUUID}/inspection/${inspectionId}/finding-details/${findingIds[currentPosition - 1]}`) : history.push(`/inspection/${inspectionId}/findings/${findingIds[currentPosition - 1]}/details`)
  };

  return (
    <section className='finding-detail container'>
      <ActionButtonsWithScrollFix
        primary={isCompleted ? editText() : findingActions.primary}
        primaryDisabled={!formContext.isValid || (!isCompleted && closurePhotoNeeded)}
        primaryTooltip={closurePhotoNeeded ? 'Add a closure photo before saving this finding.' : undefined}
        onClick={isCompleted ? handleEdit : handleSubmitClickNoMetadata}
        secondary={findingActions.secondary}
        busy={showReopenConfirmationModal || formContext.isBusy}
        spacer={false}
        extraButton={(isFindingsDateCompleted && isCompleted) || (!isFindingsDateCompleted && !isCompleted) ? false : true}
        extraButtonText={extraButtonText}
        handleExtraButtonClick={handleExtraButtonClick}
      />
      <div className='controls'>
        <h5 className='finding-link'>
          <span
            className='pointer'
            onClick={() => isPublic ? history.push(`/public/inspection/${inspectionId}/findings`) : (isGuest)? history.push(`/guest/${projectContext.guestProjectUUID}/inspection/${inspectionId}`) : history.push(`/inspection/${inspectionId}/findings`)}
          >
            <i className='fa fa-caret-left pad-right' />
            All Findings
          </span>
        </h5>
        <h3>Overview for #{findingNumber}</h3>
      </div>

      <FindingsForm
        templateName={(isGuest)? get(projectContext, 'project.template.name') : get(projectContext, 'template.name')}
        readOnly={
          findingActions.primary === null ||
          projectPermissionContext.readOnly
          || isCompleted
        }
        disableFieldRelatedToDateCompleted={
          findingActions.primary === null ||
          projectPermissionContext.readOnly
          || isFindingsDateCompleted
        }
        defaultObservation={get(
          projectContext,
          'project.client.default_finding_observation',
          '',
        )}
        inspectionId={parseInt(inspectionId)}
        templateID={(isGuest)? get(projectContext, 'project.template.id') : get(projectContext, 'template.id')}
        isCompleted={isCompleted}
        modal={false}
        setIsEnabledPhotoMetadata={setIsEnabledPhotoMetadata}
        setRequireClosurePhotos={setRequireClosurePhotos}
        requireClosurePhotos={requireClosurePhotos}
        noMetadataImage={noMetadataImage}
        showMetadataWarning={showMetadataWarning}
        setShowMetadataWarning={setShowMetadataWarning}
        isGuest={isGuest}
      />

      <DeleteFindingModal
        show={confirmDelete}
        handleClose={() => setConfirmDelete(false)}
        onDelete={onDelete}
        findingId={finding.id}
        inspectionId={inspectionId}
      />
      {showReopenConfirmationModal && (
        <FindingReopenConfirmationModal
          onClose={() => {
            setShowReopenConfirmationModal(false);
          }}
          findingId={finding.id}
          onSuccess={() => {
            formContext.set('date_completed', null);
            inspectionContext.reloadInspection();
            setIsCompleted(false);
            setIsFindingsDateCompleted(false);
          }}
        />
      )}
      {inspectionCertifiedBefore.length > 0 && (
        <ClearComplianceComfirmationModal
          onClose={() => {
            setInspectionCertifiedBefore([]);
          }
          }
          inspections={inspectionCertifiedBefore}
          Submit={() => handleSubmit()}
        />
      )}
      {typeChangeAffectedInspections.length > 0 && (
        <ClearComplianceComfirmationModal
          onClose={() => setTypeChangeAffectedInspections([])}
          inspections={typeChangeAffectedInspections}
          Submit={() => {
            setTypeChangeAffectedInspections([]);
            handleSubmitClick({ skipCompliancePrecheck: true });
          }}
        />
      )}
      <div className='center next-previous-button-height'>
        <button
          className='button-outline-tertiary'
          onClick={() => getPrevFinding()}
          disabled={disablePrev}
        >
          <i className='fa fa-angle-left pad-right' />
          Previous
        </button>{' '}
        <button
          className='button-outline-tertiary'
          onClick={() => getNextFinding()}
          disabled={disableNext}
        >
          Next
          <i className='fa fa-angle-right pad-left' />
        </button>
      </div>
    </section>
  );
}

function FindingDetail({ onBackClick, onSubmit, onDelete }) {
  const { userHasPermission } = useContext(RolesContext);
  const { inspection } = useContext(InspectionContext);
  const { inspectionsCertDue, fetchStatsFuncForOF } = useContext(DashboardContext);
  const match = useRouteMatch();
  const [finding, setFinding] = useState(null);
  const [initialValue, setInitialValue] = useState(null);
  const history = useHistory();
  const appStore = useContext(AppContext);
  const user = appStore.get("user");
  const isGuest = history.location.pathname.startsWith("/guest") && user?.roleName === "Guest";

  useEffect(() => {
    findingApi.show(match.params.findingId).then((data) => {
      setFinding(data);
    });
  }, [match.params.findingId]);

  useEffect(() => {
    if (finding){
      setInitialValue(extractFindingData(finding, match.params.inspectionId));}
  }, [finding]);
  
  if (!initialValue || !finding) {
    return null;
  }

  return (
    <VersionContextProvider>
      <FormContextProvider initialValue={initialValue}>
        <VersionContext.Consumer>
          {(versionContext) => (
            <FindingDetailFormUI
              inspectionId={(isGuest && inspection)? inspection.id : match.params.inspectionId}
              finding={finding}
              onBackClick={onBackClick}
              onSubmit={() => {
                versionContext.resetDate();
                inspectionsCertDue.refetch();
                fetchStatsFuncForOF.refetch();
                onSubmit();
              }}
              onDelete={onDelete}
              permCheck={userHasPermission}
              isCompleted={
                !isNull(initialValue.date_completed) ||
                !isNull(inspection.compliance_date)
              }
              isFindingsDateCompleted={
                !isNull(initialValue.date_completed)
              }
              findingNumber={finding.number}
              isGuest={isGuest}
            />
          )}
        </VersionContext.Consumer>
      </FormContextProvider>
    </VersionContextProvider>
  );
}

export default FindingDetail;
